﻿import React, { useState, useEffect } from 'react';
import { Asset } from '../models/asset';
import { Bin } from '../models/Locations/bin';
import { AssetData } from '../models/asset-data';
import { LookupItem } from '../models/lookup-item';
import { DataTemplates } from '../templates/data-templates';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import {
    DateBox,
    TextBox,
    SelectBox,
    CheckBox
} from 'devextreme-react';

export interface Props {
    isReadOnly: boolean;
    asset: Asset;
    assetData?: AssetData;
    bins?: Bin[];
    onValueChanged?: (asset: Asset) => void;
    isChildAsset: boolean;
}

export default function AssetHeaderComponent(props: Props) {
    const [asset, setAsset] =
        useState<Asset>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        assignAsset();
    }, [props.asset]);

    useEffect(() => {
        if (props.onValueChanged)
            props.onValueChanged(asset);
    }, [asset]);

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="pw-margin-left-1 pw-bg-rb-100 pw-padding-1">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Asset Number</div>
                            <TextBox id="formattedAssetNumber"
                                className="textbox"
                                readOnly={props.isReadOnly || props.isChildAsset}
                                onValueChanged={valueChanged}
                                maxLength={10}
                                showClearButton={true}
                                placeholder="na" value={asset ? asset.formattedAssetNumber : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Asset Name</div>
                            <TextBox id="assetName" className="textbox"
                                onValueChanged={valueChanged} readOnly={props.isReadOnly}
                                maxLength={60} showClearButton={true}
                                placeholder="na"
                                value={asset ? asset.assetName : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">VMI Group</div>
                            <SelectBox id="vmiGroupId"
                                readOnly={props.isReadOnly || props.isChildAsset}
                                placeholder="na"
                                height='1.8rem'
                                itemRender={DataTemplates.LookupItemRender}
                                dataSource={props.assetData && props.assetData.vmiGroups ?
                                    props.assetData.vmiGroups : []}
                                valueExpr='id' displayExpr="name"
                                onValueChanged={valueChanged}
                                value={asset && asset.vmiGroupId ?
                                    asset.vmiGroupId : undefined} />
                        </div>
                    </div>

                    <div className="pw-form-unit">
                        <div className="pw-form-unit-label">Description</div>
                        <TextBox id="description" className="textbox" readOnly={props.isReadOnly}
                            onValueChanged={valueChanged}
                            showClearButton={true}
                            placeholder="na" value={asset ? asset.description : undefined} />
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit pw-comp-15 pw-margin-top-1">
                                <CheckBox id="validationRequired" readOnly={props.isReadOnly}
                                    text="Validation Required"
                                    onValueChanged={valueChanged}
                                    value={asset ? asset.validationRequired : false} />
                            </div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Validation Date</div>
                            <DateBox id="validationDate" type="date" readOnly={props.isReadOnly}
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={valueChanged}
                                value={asset ? asset.validationDate : undefined}
                                placeholder="na" />
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Revalidation Date</div>
                            <DateBox id="revalidationDate" type="date" readOnly={asset?.validationRequired ? props.isReadOnly : true}
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={valueChanged}
                                value={asset ? asset.revalidationDate : undefined}
                                placeholder="na" />
                        </div>
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Vendor</div>
                            <SelectBox id="vendorId" placeholder="na" readOnly={props.isReadOnly}
                                showClearButton={true}
                                itemRender={DataTemplates.VendorTemplate}
                                dataSource={props.assetData && props.assetData.vendors ?
                                    props.assetData.vendors : []}
                                onValueChanged={valueChanged}
                                valueExpr={'vendorId'} displayExpr="name"
                                value={asset && asset.vendorId ?
                                    asset.vendorId : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Contract Agency</div>
                            <SelectBox id="contractAgencyId" readOnly={props.isReadOnly}
                                placeholder="na"
                                showClearButton={true}
                                itemRender={DataTemplates.VendorTemplate}
                                dataSource={props.assetData && props.assetData.contractAgencies ?
                                    props.assetData.contractAgencies : []}
                                onValueChanged={valueChanged}
                                valueExpr={'vendorId'} displayExpr="name"
                                value={asset && asset.contractAgencyId ?
                                    asset.contractAgencyId : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Department</div>
                            <SelectBox id="departmentId" readOnly={props.isReadOnly}
                                placeholder="na"
                                showClearButton={true}
                                itemRender={DataTemplates.DepartmentTemplate}
                                dataSource={props.assetData && props.assetData.departments ?
                                    props.assetData.departments : []}
                                valueExpr={'departmentId'} displayExpr="name"
                                onValueChanged={valueChanged}
                                value={asset && asset.departmentId ?
                                    asset.departmentId : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Zone</div>
                            <SelectBox id="zoneId" readOnly={props.isReadOnly}
                                placeholder="na" showClearButton={true}
                                itemRender={DataTemplates.LocationTemplate}
                                dataSource={props.assetData && props.assetData.zones ?
                                    props.assetData.zones : []}
                                onValueChanged={valueChanged}
                                valueExpr='zoneId' displayExpr="name"
                                value={asset && asset.zoneId ?
                                    asset.zoneId : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Bin</div>
                            <SelectBox id="binId" readOnly={props.isReadOnly}
                                placeholder="na" showClearButton={true}
                                itemRender={DataTemplates.LocationTemplate}
                                dataSource={props.bins ? props.bins : []}
                                onValueChanged={valueChanged}
                                valueExpr='binId' displayExpr="name"
                                value={asset && asset.binId ?
                                    asset.binId : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Model Number</div>
                            <TextBox id="modelNumber" className="textbox" readOnly={props.isReadOnly}
                                onValueChanged={valueChanged}
                                maxLength={100}
                                showClearButton={true}
                                placeholder="na" value={asset ? asset.modelNumber : undefined} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Vendor Serial Number</div>
                            <TextBox id="vendorSerialNumber" className="textbox"
                                onValueChanged={valueChanged} readOnly={props.isReadOnly}
                                maxLength={10}
                                showClearButton={true}
                                placeholder="na" value={asset ? asset.vendorSerialNumber : undefined} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Decommissioned Date</div>
                            <DateBox id="decommissionedDate" type="date" readOnly={props.isReadOnly}
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={valueChanged}
                                value={asset ? asset.decommissionedDate : undefined}
                                placeholder="na" />
                        </div>
                    </div>
                    {/*
                    <div className="pw-comp-row pw-margin-top-05">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Status</div>
                            <SelectBox id="statusId" readOnly={props.isReadOnly}
                                placeholder="na" height='2.1em'
                                itemRender={DataTemplates.LookupItemRender}
                                dataSource={props.assetData && props.assetData.statuses ?
                                    props.assetData.statuses : []}
                                valueExpr='id' displayExpr="name"
                                onValueChanged={valueChanged}
                                value={asset ? asset.statusId : undefined} />
                        </div>
                       
                    </div>
                    */}


                </div>
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div >
    );

    function valueChanged(e: any) {
        if (e.element && e.element.id) {
            const caller = e.element.id;
            const newValue = e.value;

            if (caller === "formattedAssetNumber") {
                // Update both the string for the textbox and the int for the logic
                setAsset({
                    ...asset,
                    formattedAssetNumber: newValue,
                    assetNumber: newValue ? parseInt(newValue, 10) : 0
                });
            } else {
                setAsset({ ...asset, [caller]: newValue });
            }

        }
    }

    function assignAsset() {
        setAsset(props.asset);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

